<template>
  <div>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-6">
        <v-col cols="12">
          <v-card elevation="3" rounded="xl">
            <!-- Page Header -->
            <v-card-title class="pb-3 pt-5 px-6">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center mb-2">
                    <v-btn
                      small
                      color="primary"
                      rounded
                      @click="$router.back()"
                      class="mr-4"
                    >
                      <v-icon left small>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                    <div>
                      <h2 class="text-h5 font-weight-bold primary--text mb-1">
                        {{ isDeletePage() != true ? 'User Transactions' : 'Deleted Transactions' }}
                      </h2>
                      <p class="text-body-2 grey--text ma-0">
                        {{ isDeletePage() != true ? 'View and filter user transaction history' : 'Restore deleted transaction records' }}
                      </p>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end align-center">
                    <v-btn
                      color="success"
                      elevation="2"
                      rounded
                      @click="exportToExcel"
                      class="px-4"
                    >
                      <v-icon left small>mdi-microsoft-excel</v-icon>
                      Export Transactions
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider class="mx-6"></v-divider>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-2 pt-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-3" color="primary">mdi-filter-variant</v-icon>
                    <span class="text-subtitle-1 font-weight-medium">Filter Transactions</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      small
                      rounded="pill"
                      class="mr-3"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active Filter(s)
                    </v-chip>
                    <v-btn
                      text
                      small
                      color="error"
                      rounded
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left small>mdi-filter-remove</v-icon>
                      Clear All
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-6 pb-6">
              <v-row class="align-center">
                <!-- Status Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-select
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Active Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    rounded
                    dense
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>

                <!-- Transaction Type Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-select
                    v-model="filter.transaction_type"
                    :items="transactionTypeItems"
                    label="Transaction Type"
                    prepend-inner-icon="mdi-swap-horizontal"
                    outlined
                    rounded
                    dense
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>

                <!-- Transaction Purpose Filter -->
                <v-col cols="12" sm="6" md="4">
                  <v-select
                    v-model="filter.transaction_purpose"
                    :items="transactionPurposeItems"
                    label="Transaction Purpose"
                    prepend-inner-icon="mdi-target"
                    outlined
                    rounded
                    dense
                    clearable
                    @change="(page = 1), getData()"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Transactions Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" rounded="lg">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="24">mdi-cash-multiple</v-icon>
                    <span class="text-h6 font-weight-medium">Transactions List</span>
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined class="mr-3">
                      <v-icon left small>mdi-receipt</v-icon>
                      {{ count }} {{ count === 1 ? "Transaction" : "Transactions" }} Found
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      Showing {{ (page - 1) * itemsPerPage + 1 }} -
                      {{ Math.min(page * itemsPerPage, count) }} of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-3">
                      <span class="text-caption grey--text mr-2">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn small outlined :value="10">10</v-btn>
                        <v-btn small outlined :value="25">25</v-btn>
                        <v-btn small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="60"
                  ></v-progress-circular>
                  <p class="mt-3 grey--text">Loading transactions...</p>
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="transactions"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{ item.is_active ? "mdi-check-circle" : "mdi-close-circle" }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- User Info Column -->
                <template #[`item.user_info`]="{ item }">
                  <div v-if="item.user">
                    <div class="font-weight-medium mb-1">
                      {{ `${item.user.first_name || ''} ${item.user.last_name || ''}`.trim() || "N/A" }}
                    </div>
                    <div class="text-caption grey--text" v-if="item.user.email">
                      <v-icon x-small class="mr-1">mdi-email</v-icon>
                      {{ item.user.email }}
                    </div>
                  </div>
                  <span v-else class="text-caption grey--text">No User</span>
                </template>

                <!-- Transaction Type Column -->
                <template #[`item.transaction_type`]="{ item }">
                  <v-chip
                    :color="item.transaction_type === 1 ? 'error' : 'success'"
                    small
                    outlined
                  >
                    <v-icon left x-small>
                      {{ item.transaction_type === 1 ? "mdi-arrow-up" : "mdi-arrow-down" }}
                    </v-icon>
                    {{ item.transaction_type === 1 ? "Debit" : "Credit" }}
                  </v-chip>
                </template>

                <!-- Transaction Purpose Column -->
                <template #[`item.transaction_purpose`]="{ item }">
                  <v-chip
                    small
                    color="info"
                    outlined
                  >
                    <v-icon left x-small>
                      {{ item.transaction_purpose === 1 ? "mdi-wallet" : "mdi-package-variant" }}
                    </v-icon>
                    {{ getTransactionPurpose(item.transaction_purpose) }}
                  </v-chip>
                </template>

                <!-- Amount Column -->
                <template #[`item.amount`]="{ item }">
                  <div class="text-right">
                    <div class="font-weight-bold text-h6 primary--text">
                      {{ formatCurrency(item.amount) }}
                    </div>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3">
                      {{ isDeletePage() ? "mdi-cash-remove" : "mdi-cash-multiple" }}
                    </v-icon>
                    <h3 class="text-h6 grey--text mt-4 mb-2">
                      {{ isDeletePage() ? "No Deleted Transactions" : "No Transactions Found" }}
                    </h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No transactions match your current filters. Try adjusting your search criteria."
                          : isDeletePage()
                          ? "There are no deleted transactions to restore."
                          : "This user has no transaction history yet."
                      }}
                    </p>
                    <v-btn
                      v-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-4 px-6">
              <v-row class="w-100 ma-0" align="center">
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-body-2 grey--text font-weight-medium">
                      Showing {{ (page - 1) * itemsPerPage + 1 }} to
                      {{ Math.min(page * itemsPerPage, count) }} of
                      {{ count }} transactions
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      small
                      outlined
                      rounded
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-3"
                    >
                      <v-icon small left>mdi-chevron-left</v-icon>
                      Previous
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      circle
                      class="mx-2"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      small
                      outlined
                      rounded
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-3"
                    >
                      Next
                      <v-icon small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-body-2 grey--text mr-3 font-weight-medium">Go to page:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details="auto"
                      rounded
                      hide-spin-buttons
                      class="no-spin-arrows"
                      style="max-width: 90px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <!-- Clear Success Dialog Box -->
      <v-dialog v-model="is_deleted_transaction" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Transaction deleted successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_deleted_transaction = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
      <!-- Restore Success Dialog Box -->
      <v-dialog v-model="is_restored_transaction" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Hurray!</h1>
                      <br />
                      <h3>Transaction restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_transaction = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
      <!-- Delete Confirmation Value Dialog -->
      <DeleteConfirmationDialog
        v-if="selectedId !== null"
        :itemId="selectedId"
        @delete="deleteData"
        ref="deleteDialog"
      />
    </v-container>
  </div>
</template>

<script>
import axios from "axios";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog.vue";
export default {
  components: { DeleteConfirmationDialog },
  name: "transactionsPage",
  data() {
    return {
      filter: {
        is_active: "",
        search_keyword: "",
        transaction_type: "",
        transaction_purpose: "",
        show_deleted: false,
      },
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      jumpToPage: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      transactions: [],
      statuses: [
        {
          text: "All Status",
          value: "",
        },
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      transactionTypeItems: [
        {
          text: "All Transaction Types",
          value: "",
        },
        { text: "Debit", value: 1 },
        { text: "Credit", value: 2 },
      ],
      transactionPurposeItems: [
        {
          text: "All Transaction Purposes",
          value: "",
        },
        { text: "Wallet", value: 1 },
        { text: "Product", value: 2 },
      ],
      isLoading: false,
      delete_transaction: false,
      is_deleted_transaction: false,
      is_restored_transaction: false,
      selectedId: null,
      headers: [
        { text: "Sr No.", value: "sr_no", sortable: false, width: "70px" },
        { text: "Status", value: "is_active", width: "120px" },
        {
          text: "User Information",
          value: "user_info",
          sortable: false,
          width: "200px",
        },
        {
          text: "Transaction Type",
          value: "transaction_type",
          sortable: false,
          width: "150px",
        },
        {
          text: "Transaction Purpose",
          value: "transaction_purpose",
          sortable: false,
          width: "150px",
        },
        {
          text: "Amount",
          value: "amount",
          sortable: false,
          width: "150px",
          align: "right",
        },
      ],
    };
  },
  computed: {
    hasActiveFilters() {
      return (
        this.filter.is_active ||
        this.filter.transaction_type ||
        this.filter.transaction_purpose
      );
    },
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      console.log("Getting transactions data...");
      this.isLoading = true;

      // Set filters with default values
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;

      const filters = {
        page: this.page,
        rowsPerPage: this.itemsPerPage,
        user_id: this.decryptIt(this.$route.params.userId),
        is_active: this.filter.is_active || "",
        transaction_type: this.filter.transaction_type || "",
        transaction_purpose: this.filter.transaction_purpose || "",
        show_deleted: this.filter.show_deleted,
      };

      let query = new URLSearchParams(filters);

      try {
        const response = await axios.get(`user_transactions?${query}`);
        this.transactions = response.data.data || [];
        this.count = response.data.count || 0;
        this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
        this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
      } catch (error) {
        console.error("Failed to fetch transactions:", error);
        this.transactions = [];
        this.count = 0;
      }

      this.isLoading = false;
    },

    clearAllFilters() {
      this.filter = {
        is_active: "",
        search_keyword: "",
        transaction_type: "",
        transaction_purpose: "",
        show_deleted: this.filter.show_deleted,
      };
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.is_active) count++;
      if (this.filter.transaction_type) count++;
      if (this.filter.transaction_purpose) count++;
      return count;
    },

    isDeletePage() {
      return this.$route.query.open_trash;
    },

    changeItemsPerPageToggle(value) {
      if (value !== null && value !== undefined) {
        this.itemsPerPage = value;
        this.selectedItemsPerPage = value;
        this.page = 1;
        this.getData();
      }
    },

    goToPage() {
      const pageNum = parseInt(this.jumpToPage);
      if (pageNum >= 1 && pageNum <= this.paginationLength) {
        this.page = pageNum;
      } else {
        this.jumpToPage = this.page;
      }
    },

    exportToExcel() {
      console.log("Export to Excel clicked");
      // Implement Excel export logic
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
      }).format(parseFloat(amount) || 0);
    },
    async deleteData(id) {
      this.delete_transaction = true;
      await axios
        .post(`/transactions/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.delete_transaction = false;
          this.is_deleted_transaction = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },
    async restoreData(id) {
      this.restore_transaction = true;
      await axios
        .post(`/transactions/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.restore_transaction = false;
          this.is_restored_transaction = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },
    openDeleteDialog(id) {
      this.selectedId = id;
      this.$nextTick(() => {
        this.$refs.deleteDialog.openDialog();
      });
    },
    getTransactionPurpose(data) {
      switch (data) {
        case 1:
          return "Wallet";

        case 2:
          return "Product";

        default:
          return "";
      }
    },
  },
};
</script>

<style scoped>
/* Enhanced Table Styles */
.enhanced-table >>> tbody tr {
  transition: all 0.3s ease;
}

.enhanced-table >>> tbody tr:hover {
  background-color: #f8f9fa !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.enhanced-table >>> tbody tr:nth-child(odd) {
  background-color: #fafbfc;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px 12px 0 0;
}

.enhanced-table >>> .v-data-table-header th {
  font-weight: 600 !important;
  color: #2c3e50 !important;
  border-bottom: 2px solid #dee2e6 !important;
  padding: 16px 12px !important;
  font-size: 0.875rem !important;
}

.enhanced-table >>> tbody td {
  padding: 16px 12px !important;
  border-bottom: 1px solid #f1f3f4 !important;
}

/* Card Enhancements */
.v-card {
  border-radius: 12px !important;
  border: 1px solid #f1f3f4;
}

.v-card--outlined {
  border: 2px solid #e3f2fd !important;
}

/* Chip Enhancements */
.v-chip {
  font-weight: 500;
  border-radius: 8px !important;
}

.v-chip--pill {
  border-radius: 50px !important;
}

/* Button Enhancements */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.02em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.v-btn--rounded {
  border-radius: 8px !important;
}

/* Input Field Enhancements */
.v-text-field--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

.v-text-field--outlined.v-input--is-focused >>> .v-input__control > .v-input__slot {
  border-color: #1976d2 !important;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.v-select--outlined >>> .v-input__control > .v-input__slot {
  border-radius: 8px !important;
  transition: all 0.3s ease;
}

/* Pagination Enhancements */
.v-pagination >>> .v-pagination__item {
  border-radius: 8px !important;
  margin: 0 2px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.v-pagination >>> .v-pagination__item:hover {
  background-color: #e3f2fd !important;
  transform: translateY(-1px);
}

.v-pagination >>> .v-pagination__item--active {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  color: white !important;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.4);
}

/* Hide number input spin buttons */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
  text-align: center;
}

/* Loading Animation */
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
}

.v-progress-circular {
  animation: pulse 2s infinite;
}

/* Spacing Utilities */
.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.gap-4 {
  gap: 16px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .enhanced-table >>> tbody td {
    padding: 12px 8px !important;
  }

  .v-card-title {
    padding: 16px !important;
  }

  .v-card-text {
    padding: 16px !important;
  }
}
</style>
